import { documentToReactComponents } from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import clsx from "clsx"
import Image from "next/image"

import type { IndividualBlogPostProps } from "@/app/insights/_types/insights"
import type { VideoItem } from "@/types/video"
import VideoPlayer from "@/ui/components/VideoPlayer"

const renderVideo = (entry: VideoItem | undefined | null, isInline = false) => {
  // Early return if entry is null/undefined or not a Videos type
  if (!entry || entry.__typename !== "Videos") {
    return null
  }

  const { wistiaVideo, videoLink, videoName, videoCover } = entry

  const hasWistiaVideo = Boolean(wistiaVideo?.items?.[0]?.hashed_id)

  // If we have a Wistia video ID, render using VideoPlayer component
  if (hasWistiaVideo && wistiaVideo) {
    return (
      <VideoPlayer
        videoURL={videoLink}
        wistiaVideo={wistiaVideo}
        title={videoName}
        image={videoCover?.url}
        className="w-full"
      />
    )
  }

  // If no video link is provided, return null
  if (!videoLink) {
    return null
  }

  // For regular iframe videos, use appropriate container based on inline status
  const Container = isInline ? "span" : "div"
  return (
    <Container
      className={clsx(
        "relative overflow-hidden rounded-lg aspect-video",
        !isInline && "mb-6",
      )}
    >
      <iframe
        src={videoLink}
        width="100%"
        height="100%"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        title={videoName ?? "Video"}
      />
    </Container>
  )
}

function renderOptions(links, legal = false) {
  // create an asset block map
  const assetBlockMap = new Map()
  // loop through the assets and add them to the map
  links?.assets?.block?.forEach((asset) => {
    assetBlockMap.set(asset.sys.id, asset)
  })

  // create an entry block map
  const entryBlockMap = new Map()
  // loop through the entries and add them to the map
  links?.entries?.block?.forEach((entry) => {
    entryBlockMap.set(entry.sys.id, entry)
  })
  // loop through the inline linked entries and add them to the map
  links?.entries?.inline?.forEach((entry) => {
    entryBlockMap.set(entry.sys.id, entry)
  })

  const renderNode = {
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      const { data } = node
      const entry = entryBlockMap.get(data.target.sys.id)
      return renderVideo(entry, true)
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const { data } = node
      const entry = entryBlockMap.get(data.target.sys.id)
      return renderVideo(entry, false)
    },
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const { data } = node
      // find the asset in the assetBlockMap by ID
      const asset = assetBlockMap.get(data.target.sys.id)

      // render the asset accordingly
      switch (asset.contentType) {
        case "image/apng":
        case "image/avif":
        case "image/gif":
        case "image/jpg":
        case "image/jpeg":
        case "image/jfif":
        case "image/pjpeg":
        case "image/pjp":
        case "image/png":
        case "image/svg":
        case "image/webp":
          return (
            <Image
              src={asset.url}
              width={asset.width}
              height={asset.height}
              alt={asset.description}
              itemProp="image"
              className="my-6"
            />
          )
        default:
          return null
      }
    },
  }

  if (legal) {
    renderNode[BLOCKS.HEADING_2] = (node, children) => {
      // Extract text content from children, handling both plain text and formatted text
      const extractTextContent = (items) => {
        if (!items) return ""

        // Handle single item
        const itemsList = Array.isArray(items) ? items : [items]

        return itemsList
          .map((item) => {
            // Skip false/null/undefined values
            if (item === false || item === null || item === undefined) {
              return ""
            }

            // Handle string directly
            if (typeof item === "string") {
              return item
            }

            // Handle arrays (like [false, 'text'])
            if (Array.isArray(item)) {
              return extractTextContent(item)
            }

            // Handle React elements
            if (item && typeof item === "object") {
              // Check for props.children (React elements)
              if (item.props && item.props.children !== undefined) {
                return extractTextContent(item.props.children)
              }

              // Handle special cases where text might be stored differently
              if (item.key && typeof item.key === "string") {
                return item.key
              }

              // Try toString as fallback
              if (
                typeof item.toString === "function" &&
                item.toString !== Object.prototype.toString
              ) {
                return item.toString()
              }
            }

            // Handle null, undefined, or other falsy values
            return ""
          })
          .join("")
      }

      const title = extractTextContent(children)
      const id = title
        ?.replace(/[^a-zA-Z\s]/g, "")
        ?.replace(/\s+/g, " ")
        .trim()
      return <h2 id={id}>{children}</h2>
    }
  }

  return {
    // other options...

    renderNode,
    // Replace /n with <br>
    renderText: (text) => {
      const segments = text.split("\n")
      const result = []

      segments.forEach((textSegment, index) => {
        if (index > 0) {
          // Create a unique key based on position and surrounding text content
          const prevSegment = segments[index - 1] || ""
          const keyId = `br-${prevSegment.slice(-3)}-${textSegment.slice(0, 3)}-${index}`
          result.push(<br key={keyId} />)
        }
        result.push(textSegment)
      })

      return result
    },
  }
}

export default function RichTextResponse({
  richTextResponse,
  legal = false,
}: {
  richTextResponse: IndividualBlogPostProps["contentRich"]
  legal: boolean
}) {
  return (
    <>
      {documentToReactComponents(
        richTextResponse.json,
        renderOptions(richTextResponse.links, legal),
      )}
    </>
  )
}
